import { getAuth, signInWithCustomToken, signOut } from 'firebase/auth';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import {
  BROTIA_BASE_URL,
  getBrotiaAuth,
  getBrotiaDb,
  getBrotiaStorage,
} from './firebase';

export type BrotiaAttachment = {
  index: number;
  mediaMimeType?: string;
  mediaFilename?: string;
  mediaStoragePath?: string;
};

export type BrotiaMessage = {
  id: string;
  text: string;
  direction: 'inbound' | 'outbound';
  createdAt: Date;
  messageType?: string;
  attachments: BrotiaAttachment[];
  // Legacy single-attachment fields, populated only for back-compat consumers.
  mediaMimeType?: string;
  mediaFilename?: string;
  mediaStoragePath?: string;
};

const ALLOWED_IMAGE_MIME = /^image\/(jpeg|png|gif|webp|heic|heif)$/i;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_IMAGES_PER_MESSAGE = 10;

// Maps a brotia storage path to a local object URL, so a freshly uploaded
// thumbnail renders immediately without hitting the brotia media proxy (which
// currently rejects Posy in-app sessions).
const localUploadUriMap = new Map<string, string>();

export const getLocalUploadUri = (
  storagePath: string | undefined,
): string | null => {
  if (!storagePath) return null;
  return localUploadUriMap.get(storagePath) ?? null;
};

export const clearLocalUploadCache = (): void => {
  localUploadUriMap.forEach((url) => URL.revokeObjectURL(url));
  localUploadUriMap.clear();
};

export type ChatSession = {
  conversationId: string;
  brotiaUid: string;
};

export class RateLimitedError extends Error {
  retryAfterSeconds: number;
  constructor(retryAfterSeconds: number) {
    super(`Rate limited (retry after ${retryAfterSeconds}s)`);
    this.name = 'RateLimitedError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

export class FileTooLargeError extends Error {
  filename?: string;
  constructor(filename?: string) {
    super(
      filename
        ? `Image "${filename}" is larger than 10MB`
        : 'Image is larger than 10MB',
    );
    this.name = 'FileTooLargeError';
    this.filename = filename;
  }
}

export class UnsupportedFileTypeError extends Error {
  mimeType: string;
  constructor(mimeType: string) {
    super(`Unsupported file type: ${mimeType}`);
    this.name = 'UnsupportedFileTypeError';
    this.mimeType = mimeType;
  }
}

export class MetadataValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MetadataValidationError';
  }
}

const METADATA_KEY_PATTERN = /^[a-zA-Z0-9_.-]+$/;
const MAX_METADATA_KEYS = 20;
const MAX_METADATA_KEY_LEN = 60;
const MAX_METADATA_VALUE_LEN = 500;

const sanitizeMetadata = (
  metadata: Record<string, string | undefined | null>,
): Record<string, string> => {
  const cleaned: Record<string, string> = {};
  for (const [key, rawValue] of Object.entries(metadata)) {
    if (rawValue === undefined || rawValue === null) continue;
    const value = String(rawValue);
    if (value.length === 0) continue;
    if (key.length === 0 || key.length > MAX_METADATA_KEY_LEN) {
      throw new MetadataValidationError(
        `Metadata key "${key}" must be 1-${MAX_METADATA_KEY_LEN} characters`,
      );
    }
    if (!METADATA_KEY_PATTERN.test(key)) {
      throw new MetadataValidationError(
        `Metadata key "${key}" contains invalid characters`,
      );
    }
    if (value.length > MAX_METADATA_VALUE_LEN) {
      throw new MetadataValidationError(
        `Metadata value for "${key}" exceeds ${MAX_METADATA_VALUE_LEN} chars`,
      );
    }
    cleaned[key] = value;
  }
  if (Object.keys(cleaned).length > MAX_METADATA_KEYS) {
    throw new MetadataValidationError(
      `Metadata cannot contain more than ${MAX_METADATA_KEYS} keys`,
    );
  }
  return cleaned;
};

export const openChatSession = async (
  workspaceId: string,
  metadata?: Record<string, string | undefined | null>,
): Promise<ChatSession> => {
  const posyUser = getAuth().currentUser;
  if (!posyUser) {
    throw new Error('User must be signed in to Posy');
  }

  const posyToken = await posyUser.getIdToken();

  const cleanedMetadata = metadata ? sanitizeMetadata(metadata) : undefined;
  const hasMetadata =
    cleanedMetadata !== undefined && Object.keys(cleanedMetadata).length > 0;

  const response = await fetch(`${BROTIA_BASE_URL}/api/in-app/session`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${posyToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      workspaceId,
      ...(hasMetadata ? { metadata: cleanedMetadata } : {}),
    }),
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
    };
    throw new Error(body.error ?? `Brotia session failed (${response.status})`);
  }

  const { customToken, conversationId, brotiaUid } =
    (await response.json()) as {
      customToken: string;
      conversationId: string;
      brotiaUid: string;
    };

  const brotiaAuth = await getBrotiaAuth();
  await signInWithCustomToken(brotiaAuth, customToken);

  return { conversationId, brotiaUid };
};

export const subscribeToMessages = async (
  conversationId: string,
  onChange: (messages: BrotiaMessage[]) => void,
  onError?: (error: Error) => void,
): Promise<() => void> => {
  const db = await getBrotiaDb();
  const messagesQuery = query(
    collection(db, 'conversations', conversationId, 'messages'),
    orderBy('createdAt', 'asc'),
  );

  return onSnapshot(
    messagesQuery,
    (snapshot) => {
      const messages: BrotiaMessage[] = snapshot.docs.map((doc) => {
        const data = doc.data();
        const messageType =
          typeof data.messageType === 'string' ? data.messageType : undefined;
        const legacyMime =
          typeof data.mediaMimeType === 'string'
            ? data.mediaMimeType
            : undefined;
        const legacyFilename =
          typeof data.mediaFilename === 'string'
            ? data.mediaFilename
            : undefined;
        const legacyPath =
          typeof data.mediaStoragePath === 'string'
            ? data.mediaStoragePath
            : undefined;

        let attachments: BrotiaAttachment[] = [];
        if (Array.isArray(data.attachments) && data.attachments.length > 0) {
          attachments = (
            data.attachments as Array<Record<string, unknown>>
          ).map((raw, idx) => ({
            index: typeof raw.index === 'number' ? raw.index : idx,
            mediaMimeType:
              typeof raw.mediaMimeType === 'string'
                ? raw.mediaMimeType
                : undefined,
            mediaFilename:
              typeof raw.mediaFilename === 'string'
                ? raw.mediaFilename
                : undefined,
            mediaStoragePath:
              typeof raw.mediaStoragePath === 'string'
                ? raw.mediaStoragePath
                : undefined,
          }));
        } else if (legacyMime || legacyFilename || legacyPath) {
          attachments = [
            {
              index: 0,
              mediaMimeType: legacyMime,
              mediaFilename: legacyFilename,
              mediaStoragePath: legacyPath,
            },
          ];
        }

        return {
          id: doc.id,
          text: String(data.text ?? ''),
          direction: data.direction === 'outbound' ? 'outbound' : 'inbound',
          createdAt: data.createdAt?.toDate?.() ?? new Date(),
          messageType,
          attachments,
          mediaMimeType: legacyMime,
          mediaFilename: legacyFilename,
          mediaStoragePath: legacyPath,
        };
      });
      onChange(messages);
    },
    (error) => onError?.(error),
  );
};

export const sendMessage = async (text: string): Promise<void> => {
  const brotiaAuth = await getBrotiaAuth();
  const brotiaUser = brotiaAuth.currentUser;
  if (!brotiaUser) {
    throw new Error('Call openChatSession() first');
  }

  const brotiaToken = await brotiaUser.getIdToken();

  const response = await fetch(`${BROTIA_BASE_URL}/api/in-app/messages`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${brotiaToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ text }),
  });

  if (response.status === 429) {
    const retryAfter = Number(response.headers.get('Retry-After')) || 0;
    throw new RateLimitedError(retryAfter);
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
    };
    throw new Error(body.error ?? `Send failed (${response.status})`);
  }
};

export type SendImagesOptions = {
  caption?: string;
  onProgress?: (uploaded: number, total: number) => void;
};

// Web-native counterpart of the mobile expo-image-picker flow: the caller
// supplies the `File[]` selected via an <input type="file">; this validates,
// uploads to brotia Storage, then posts a message referencing the storage paths.
export const sendImages = async (
  conversationId: string,
  files: File[],
  options: SendImagesOptions = {},
): Promise<boolean> => {
  const selected = files.slice(0, MAX_IMAGES_PER_MESSAGE);
  if (selected.length === 0) return false;

  // Validate every file's mime type up front, before any upload starts.
  for (const file of selected) {
    const mime = file.type || 'image/jpeg';
    if (!ALLOWED_IMAGE_MIME.test(mime)) {
      throw new UnsupportedFileTypeError(mime);
    }
    if (file.size > MAX_IMAGE_BYTES) {
      throw new FileTooLargeError(file.name);
    }
  }

  const storage = await getBrotiaStorage();
  const total = selected.length;
  let uploaded = 0;
  options.onProgress?.(0, total);

  // Upload all images in parallel.
  const attachments = await Promise.all(
    selected.map(async (file, idx) => {
      const mimeType = file.type || 'image/jpeg';
      const ext = mimeType.split('/')[1] ?? 'jpg';
      const rawFilename = file.name || `image-${idx}.${ext}`;
      const safeFilename = rawFilename.replace(/[^a-zA-Z0-9._-]/g, '_');

      const storagePath = `in-app-uploads/${conversationId}/${idx}-${safeFilename}`;

      // Remember the local preview URL before upload so each thumbnail renders
      // immediately (revoked when the session cache is cleared on sign-out).
      localUploadUriMap.set(storagePath, URL.createObjectURL(file));

      await uploadBytes(ref(storage, storagePath), file, {
        contentType: mimeType,
      });

      uploaded += 1;
      options.onProgress?.(uploaded, total);

      return {
        mediaStoragePath: storagePath,
        mediaMimeType: mimeType,
        mediaFilename: safeFilename,
      };
    }),
  );

  const brotiaAuth = await getBrotiaAuth();
  const brotiaUser = brotiaAuth.currentUser;
  if (!brotiaUser) {
    throw new Error('Call openChatSession() first');
  }
  const brotiaToken = await brotiaUser.getIdToken();

  const response = await fetch(`${BROTIA_BASE_URL}/api/in-app/messages`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${brotiaToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      text: options.caption ?? '',
      attachments,
    }),
  });

  if (response.status === 429) {
    const retryAfter = Number(response.headers.get('Retry-After')) || 0;
    throw new RateLimitedError(retryAfter);
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
    };
    throw new Error(body.error ?? `Send failed (${response.status})`);
  }

  return true;
};

export const signOutOfBrotia = async (): Promise<void> => {
  const brotiaAuth = await getBrotiaAuth();
  await signOut(brotiaAuth);
};
